import { DomainValidationError } from "./identifiers.js";
import { MEMORY_PROVENANCE_RANK, classifyMemoryValue, isMemoryRefinement, memorySensitiveUnsupportedClasses, memoryValuesConflict, } from "./memory-taxonomy.js";
// The Phase 5 memory write policy (docs/architecture/phase-5-memory.md, section 4; ADR 0005). One pure
// function decides what a model's candidate becomes. It has no clock, no I/O and no randomness. The
// model labels a statement; this code decides. Confidence is metadata: it can block a promotion and can
// never permit one. A decision holds a rule id and a closed outcome, never a sentence about the value.
export const MEMORY_POLICY_VERSION = "memory-policy.v1";
export const memoryWriteOutcomes = Object.freeze([
    "NEW_ACTIVE",
    "NEW_CANDIDATE_REVIEW",
    "REINFORCE",
    "PROPOSE_UPDATE",
    "PROPOSE_CONFLICT",
    "PROPOSE_RETRACTION",
    "NO_OP_TRANSIENT",
    "NO_OP_UNCERTAIN",
    "NO_OP_DUPLICATE",
    "NO_OP_OUTRANKED",
    "NO_OP_NO_TARGET",
    "NO_OP_PERSONALIZATION_OFF",
    "REJECT_SENSITIVE",
    "REJECT_UNSUPPORTED",
    "REJECT_NO_EVIDENCE",
]);
const decision = (outcome, ruleId, write = null) => Object.freeze({ outcome, policyVersion: MEMORY_POLICY_VERSION, ruleId, write });
const STATUS_ORDER = {
    ACTIVE: 0,
    DISPUTED: 1,
    CANDIDATE: 2,
};
/** Highest-trust first within a status, then the lower id, so a choice among several facts is deterministic. */
function byPreference(a, b) {
    return (STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
        MEMORY_PROVENANCE_RANK[b.provenance] - MEMORY_PROVENANCE_RANK[a.provenance] ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
/**
 * The scope the policy trusts (design 4.3). A temporal marker without a habitual marker means "for now"
 * even when the model said durable, and even with a remember marker ("Remember, tonight I want vegetarian").
 * The markers are read from the governing sentence, not from the model's quote (Ruling R8). A sentence with both
 * kinds of word keeps the model's scope here and is stopped later, in promotion (Ruling R9).
 * Otherwise the model's scope stands, and a durable scope also needs a stability label or a remember marker.
 */
function effectiveScope(candidate) {
    const { remember, habitual, temporal } = candidate.evidence.markers;
    if (temporal && !habitual)
        return "TURN";
    if (candidate.scope !== "DURABLE")
        return candidate.scope;
    return candidate.stability !== "NONE" || remember ? "DURABLE" : "TURN";
}
/** The first reason auto-promotion fails (design 4.5), or `null` when every condition holds. */
function autoPromotionBlock(candidate, value, deletedKinds) {
    const { remember, habitual, temporal } = candidate.evidence.markers;
    if (candidate.basis !== "STATED")
        return "INFERRED";
    if (value.autoPromotion === "NEVER")
        return value.form === "TEXT" ? "FREE_TEXT" : "KIND_REQUIRES_REVIEW";
    // Ruling R9: a sentence with both a habitual and a temporal word ("I always eat vegetarian but tonight fish") has no
    // clear scope. A human reads it; a remember request does not clear the doubt.
    if (temporal && habitual)
        return "SCOPE_AMBIGUOUS";
    if (value.autoPromotion === "EXPLICIT_REMEMBER_ONLY") {
        if (candidate.stability !== "EXPLICIT_REMEMBER" || !remember)
            return "PERSONAL_NEEDS_REMEMBER";
    }
    else {
        const statedStable = candidate.stability === "EXPLICIT_REMEMBER" ||
            candidate.stability === "HABITUAL" ||
            candidate.stability === "IDENTITY";
        if (!statedStable || !(remember || habitual || candidate.stability === "IDENTITY")) {
            return "LOW_CONFIDENCE";
        }
    }
    // Confidence only blocks. A value that is not a whole number from 500 to 1000 (NaN, a fraction, a negative number, an
    // infinity) never passes: `!(x >= 500)` and `x < 500` differ for NaN.
    const confidence = candidate.confidencePermille;
    if (confidence !== null &&
        !(Number.isInteger(confidence) && confidence >= 500 && confidence <= 1000)) {
        return "LOW_CONFIDENCE";
    }
    if (candidate.kind !== null && deletedKinds.includes(candidate.kind)) {
        return "RECENT_CUSTOMER_DELETION";
    }
    return null;
}
/**
 * `EXPLICIT_REMEMBER_REQUEST` only when the model said "remember" AND the quote holds a remember word: a
 * model cannot claim a request the customer did not make. An inference is `APPROVED_EXTRACTION`.
 */
function sourceTypeOf(candidate) {
    if (candidate.basis !== "STATED")
        return "APPROVED_EXTRACTION";
    return candidate.stability === "EXPLICIT_REMEMBER" && candidate.evidence.markers.remember
        ? "EXPLICIT_REMEMBER_REQUEST"
        : "EXPLICIT_CUSTOMER_STATEMENT";
}
const coversEvidence = (fact, candidate) => {
    const ids = candidate.evidenceMessageIds ?? [];
    const have = fact.evidenceMessageIds ?? [];
    return ids.length > 0 && ids.every((id) => have.includes(id));
};
/**
 * Decides what a candidate becomes: the decision table of design 4.2, first matching rule wins. A wrong
 * `policyVersion` throws. Equal inputs give equal decisions.
 */
export function decideMemoryWrite(candidate, context, policyVersion) {
    if (policyVersion !== MEMORY_POLICY_VERSION) {
        throw new DomainValidationError("policyVersion", "is not the supported memory policy version");
    }
    // D1, D2: a class the taxonomy does not store, or a statement about someone else.
    if (candidate.unsupportedClass !== null) {
        return memorySensitiveUnsupportedClasses.includes(candidate.unsupportedClass)
            ? decision("REJECT_SENSITIVE", "D1")
            : decision("REJECT_UNSUPPORTED", "D2");
    }
    if (candidate.assertion === "THIRD_PARTY" || candidate.kind === null) {
        return decision("REJECT_UNSUPPORTED", "D2");
    }
    const { kind } = candidate;
    // D3: the quote must be in a cited customer message.
    if (!candidate.evidence.grounded)
        return decision("REJECT_NO_EVIDENCE", "D3");
    // D4, D4b: the value must be a vocabulary code, or an allowed and non-sensitive text.
    const value = classifyMemoryValue(kind, candidate.value);
    if (!value.ok) {
        return value.reason === "SENSITIVE_TEXT"
            ? decision("REJECT_SENSITIVE", "D4")
            : decision("REJECT_UNSUPPORTED", "D4b");
    }
    // D5: the customer turned personalization off.
    if (context.controls.paused || context.controls.disabledKinds.includes(kind)) {
        return decision("NO_OP_PERSONALIZATION_OFF", "D5");
    }
    // D6: a hedge, a hypothesis, a question or sarcasm is never memory. The domain never upgrades the label.
    if (candidate.assertion !== "CERTAIN")
        return decision("NO_OP_UNCERTAIN", "D6");
    // D7: a statement for now is not durable memory.
    if (effectiveScope(candidate) !== "DURABLE")
        return decision("NO_OP_TRANSIENT", "D7");
    const target = { kind, normalizedValue: value.normalizedValue };
    const sameValue = [...context.openFacts]
        .filter((fact) => fact.kind === kind && fact.normalizedValue === value.normalizedValue)
        .sort(byPreference);
    // D8, D8b, D8c: the customer takes a fact back. The job only records the evidence; a human decides. A model-inferred
    // retraction never contests a fact that a person confirmed (Ruling R11): only a statement by the customer can.
    if (candidate.polarity === "RETRACT") {
        const retracted = sameValue[0];
        if (retracted === undefined)
            return decision("NO_OP_NO_TARGET", "D8b");
        if (candidate.basis === "INFERRED" &&
            MEMORY_PROVENANCE_RANK[retracted.provenance] >= MEMORY_PROVENANCE_RANK.STAFF_CONFIRMED) {
            return decision("NO_OP_OUTRANKED", "D8c");
        }
        return decision("PROPOSE_RETRACTION", "D8", { evidenceFor: retracted.id, event: "RETRACTION" });
    }
    // D9: the same value is already open. Nothing new is written, only evidence.
    const duplicate = sameValue[0];
    if (duplicate !== undefined) {
        return coversEvidence(duplicate, candidate)
            ? decision("NO_OP_DUPLICATE", "D9")
            : decision("REINFORCE", "D9", { evidenceFor: duplicate.id, event: "REINFORCE" });
    }
    const insertBase = {
        kind,
        normalizedValue: value.normalizedValue,
        rawValue: candidate.value.trim(),
        sourceType: sourceTypeOf(candidate),
        sensitivity: value.sensitivity,
    };
    // D10: a different value of the same need. The job never overwrites a fact: it proposes.
    const conflicting = [...context.openFacts]
        .filter((fact) => fact.status !== "CANDIDATE" &&
        memoryValuesConflict({ kind: fact.kind, normalizedValue: fact.normalizedValue }, target))
        .sort(byPreference)[0];
    if (conflicting !== undefined) {
        if (candidate.basis === "INFERRED" &&
            MEMORY_PROVENANCE_RANK[conflicting.provenance] >=
                MEMORY_PROVENANCE_RANK.CUSTOMER_STATED_IN_CHAT) {
            return decision("NO_OP_OUTRANKED", "D10");
        }
        const update = candidate.changeSignal === "EXPLICIT_CHANGE" ||
            isMemoryRefinement({ kind: conflicting.kind, normalizedValue: conflicting.normalizedValue }, target);
        return update
            ? decision("PROPOSE_UPDATE", "D10b", {
                insert: {
                    ...insertBase,
                    status: "CANDIDATE",
                    reviewReason: "UPDATE_PROPOSED",
                    conflictsWithFactId: conflicting.id,
                },
            })
            : decision("PROPOSE_CONFLICT", "D10c", {
                insert: {
                    ...insertBase,
                    status: "CANDIDATE",
                    reviewReason: "CONFLICT",
                    conflictsWithFactId: conflicting.id,
                },
            });
    }
    // D11, D12: auto-promotion, or a review candidate with the first reason that blocked it. A conflicting
    // candidate that waits for review also blocks it (Ruling R2): two contradicting facts never become ACTIVE together.
    const block = autoPromotionBlock(candidate, value, context.recentCustomerDeletionKinds);
    const conflictingCandidate = [...context.openFacts]
        .filter((fact) => fact.status === "CANDIDATE" &&
        memoryValuesConflict({ kind: fact.kind, normalizedValue: fact.normalizedValue }, target))
        .sort(byPreference)[0];
    if (block === null && conflictingCandidate === undefined) {
        return decision("NEW_ACTIVE", "D11", {
            insert: { ...insertBase, status: "ACTIVE", reviewReason: null, conflictsWithFactId: null },
        });
    }
    return decision("NEW_CANDIDATE_REVIEW", "D12", {
        insert: {
            ...insertBase,
            status: "CANDIDATE",
            reviewReason: block ?? "CONFLICTS_WITH_CANDIDATE",
            // Ruling R2: a candidate that would pass every promotion step but meets a conflicting CANDIDATE is linked to it.
            conflictsWithFactId: block === null ? (conflictingCandidate?.id ?? null) : null,
        },
    });
}
